import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setPageTitle } from '@/utils/pageTitle';
import { FaHome, FaBars, FaTimes, FaChartBar } from 'react-icons/fa';
import { useAuth } from '@/context/AuthContext';
import { employeeNamesService } from '@/services/employeeNames';
import { UserInfoSection } from '@/components/layout/UserInfoSection';
import NavigationMenu, { NavigationSection } from './NavigationMenu';
import { LoadingIndicator } from './LoadingIndicator';
import Dashboard from '@/pages/asin-sales-history/Dashboard';
import OverallSalesHistory from '@/pages/asin-sales-history/OverallSalesHistory';
import ASINViewer from '@/pages/asin-sales-history/ASINViewer';

const ASINSalesHistoryLayout: React.FC = () => {
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const [displayName, setDisplayName] = useState<string>('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<NavigationSection>('asin-sales-history');
  const [targetSection, setTargetSection] = useState<NavigationSection>('asin-sales-history');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    setPageTitle('Sales & Profit History');
  }, []);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  const handleSectionChange = (section: NavigationSection) => {
    if (section === activeSection) return;
    
    // Start transition
    setTargetSection(section);
    setIsTransitioning(true);
    setLoadingProgress(0);
    
    // Use auto-progress for smooth animation
    setTimeout(() => {
      setActiveSection(section);
      setIsTransitioning(false);
      setLoadingProgress(0);
    }, 1200); // Realistic timing for page transitions
  };

  const renderActiveContent = () => {
    const getSectionName = (section: NavigationSection) => {
      switch (section) {
        case 'asin-sales-history':
          return 'ASIN Sales Data';
        case 'overall-sales-history':
          return 'Overall Sales Data';
        case 'asin-viewer':
          return 'ASIN Viewer';
        default:
          return 'Loading';
      }
    };

    const content = () => {
      switch (activeSection) {
        case 'asin-sales-history':
          return <Dashboard />;
        case 'overall-sales-history':
          return <OverallSalesHistory />;
        case 'asin-viewer':
          return <ASINViewer />;
        default:
          return <Dashboard />;
      }
    };

    return (
      <div className="relative">
        {/* Overlay loader for page transitions */}
        {isTransitioning && (
          <LoadingIndicator
            message={`Loading ${getSectionName(targetSection)}`}
            progress={undefined}
            overlay={true}
            size="md"
          />
        )}
        
        {/* Main content with conditional dimming */}
        <div className={`transition-opacity duration-200 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
          {content()}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <FaTimes className="h-6 w-6 text-gray-600" />
          ) : (
            <FaBars className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          w-64 md:w-64
        `}
      >
        {/* Header */}
        <div
          className="border-b flex justify-center items-center h-23 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/sales-and-profit-history')}
          title="Sales & Profit History Dashboard"
        >
          <div className="flex flex-col items-center justify-center py-4">
            <FaChartBar className="h-10 w-10 text-violet-600 mb-1" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Sales & Profit History</h1>
          </div>
        </div>
        
        {/* Navigation Menu */}
        <NavigationMenu
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
        />

        {/* Separator */}
        <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

        {/* Apps Hub Link */}
        <div
          onClick={() => handleNavigate('/dashboard')}
          className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
        >
          <FaHome className="mr-3 h-5 w-5" />
          Apps Hub
        </div>

        {/* User Info */}
        <UserInfoSection displayName={displayName} />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        {renderActiveContent()}
      </main>
    </div>
  );
};

export default ASINSalesHistoryLayout;